import { MarketCountryCode } from '@axo/shared/types';
import { QueryClient } from 'react-query';
import { consentKeys } from '@axo/shared/data-access/hooks';
import {
  CommunicationChannelType,
  ConsentData,
} from '@axo/shared/data-access/types/consent';
import { AppToaster } from '../../AppToaster/AppToaster';

/**
 * True when the partner has a real (backend-provided) ai_phone marketing item.
 *
 * Reads the RAW consent items (`consentItems.data.Items`) on purpose:
 * `normalizeConsentItems` mutates the per-partner arrays held in
 * `groupedConsentItems` in place, unshifting a synthesized
 * `{ Channel: AiPhone, Transactional: false }` row for partners without one.
 * Reading the normalized/grouped source would match that synthetic row and
 * return a false positive for legacy customers (no migrated ai_phone item),
 * causing the bulk revoke to send `ai_phone` → the change endpoint 404s on the
 * missing item and the whole request fails, leaving Email/SMS/Phone unrevoked.
 */
export const hasRealAiPhoneMarketing = (
  rawConsentItems: ConsentData[] | undefined,
  partnerId?: number
): boolean =>
  !!rawConsentItems?.some(
    (item) =>
      item.PartnerID === partnerId &&
      item.Channel === CommunicationChannelType.AiPhone &&
      !item.Transactional
  );

export const handleConsentCreateSuccess = (
  country: MarketCountryCode,
  customerID: string,
  queryClient: QueryClient
) => {
  queryClient.invalidateQueries(consentKeys.items(country, customerID));

  AppToaster.then((t) =>
    t.show({
      intent: 'success',
      message: 'SMS/Email consent added successfully',
    })
  );
};

export const handleConsentCreateError = (error: unknown) => {
  const errorMessage = error instanceof Error ? error.message : 'Unknown error';

  AppToaster.then((t) =>
    t.show({
      intent: 'danger',
      message: `Failed to add consent: ${errorMessage}`,
    })
  );
};
